import type { FeatureFlagValues } from '@seen/core/featureFlags'
import { useQuery } from '@seen/react'
import type { UseQueryResult } from '@tanstack/react-query'
import { parseResponse } from 'hono/client'
import { usePortalAiClient } from '#src/api/ai.ts'

const FEATURE_FLAG_REQUEST_TIMEOUT_MS = 10_000

export function createFeatureFlagRequestSignal(
  querySignal: AbortSignal,
  timeoutMs = FEATURE_FLAG_REQUEST_TIMEOUT_MS
): AbortSignal {
  return AbortSignal.any([querySignal, AbortSignal.timeout(timeoutMs)])
}

export function boundFeatureFlagRequest<T>(
  request: Promise<T>,
  signal: AbortSignal
): Promise<T> {
  if (signal.aborted) {
    // oxlint-disable-next-line typescript/prefer-promise-reject-errors -- rejects with the signal's own abort reason, as fetch does
    return Promise.reject(signal.reason)
  }

  return new Promise<T>((resolve, reject) => {
    // oxlint-disable-next-line typescript/prefer-promise-reject-errors -- rejects with the signal's own abort reason, as fetch does
    const onAbort = () => reject(signal.reason)
    signal.addEventListener('abort', onAbort, { once: true })
    void request.then(
      (value) => {
        signal.removeEventListener('abort', onAbort)
        resolve(value)
      },
      (cause: unknown) => {
        signal.removeEventListener('abort', onAbort)
        // oxlint-disable-next-line typescript/prefer-promise-reject-errors -- forwards the wrapped request's rejection unchanged
        reject(cause)
      }
    )
  })
}

export function getFeatureFlagValuesQueryKey(
  practitionerReference: string | undefined
) {
  return ['seen-api', 'featureFlags', practitionerReference] as const
}

export function useFeatureFlagValues(
  practitionerReference: string | undefined
): UseQueryResult<FeatureFlagValues> {
  const client = usePortalAiClient()

  return useQuery({
    enabled: practitionerReference !== undefined,
    queryKey: getFeatureFlagValuesQueryKey(practitionerReference),
    queryFn: async ({ signal }) => {
      const requestSignal = createFeatureFlagRequestSignal(signal)
      const response = await boundFeatureFlagRequest(
        parseResponse(
          client.api.featureFlags.$get(undefined, {
            init: { signal: requestSignal },
          })
        ),
        requestSignal
      )
      return response
    },
    retry: false,
  })
}
